import React, { useEffect, useState } from 'react';
import { Search, Plus, Edit2, Trash2, Truck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import OwnerSelect from '../components/OwnerSelect';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

const emptyProvider = {
  name: '', nif: '', email: '', phone: '', address: '', city: '',
  postalCode: '', province: '', notes: '',
  isActive: true, isAccountant: false, isCurrentAccountant: false,
  ownerId: null,
};

const Providers = () => {
  const { hasPermission } = useAuth();
  const modal = useModal();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useUserPreference('providers:search', '');
  const [viewMode, setViewMode] = useViewMode('providers');
  const fl = useFieldLayout('providers', viewMode);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyProvider });
  const [providerToDelete, setProviderToDelete] = useState(null);

  useEffect(() => { loadProviders(); }, []);

  const loadProviders = async () => {
    try {
      const res = await api.get('/providers');
      setProviders(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      modal.error('Error cargando proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      modal.error('El nombre es obligatorio');
      return;
    }
    try {
      if (editing) {
        await api.put(`/providers/${editing}`, form);
      } else {
        await api.post('/providers', form);
      }
      await loadProviders();
      closeModal();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando proveedor');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/providers/${id}`);
      await loadProviders();
      setProviderToDelete(null);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error borrando proveedor');
    }
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name || '', nif: p.nif || '', email: p.email || '', phone: p.phone || '',
      address: p.address || '', city: p.city || '', postalCode: p.postalCode || '',
      province: p.province || '', notes: p.notes || '',
      isActive: p.isActive !== false,
      isAccountant: !!p.isAccountant,
      isCurrentAccountant: !!p.isCurrentAccountant,
      ownerId: p.ownerId ?? null,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm({ ...emptyProvider });
  };

  const filtered = providers.filter(p =>
    (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.nif || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.phone || '').toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { key: 'name', header: 'Nombre', className: 'dt-strong', fixed: true, render: (p) => (
      <><span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.8em' }}>#{p.id}</span> {p.name}</>
    ) },
    { key: 'city', header: 'Ciudad', render: (p) => p.city || '—' },
    { key: 'nif', header: 'NIF', className: 'dt-nowrap', render: (p) => p.nif || '—' },
    { key: 'email', header: 'Email', render: (p) => p.email || '—' },
    { key: 'phone', header: 'Teléfono', className: 'dt-nowrap', render: (p) => p.phone || '—' },
    { key: 'address', header: 'Dirección', render: (p) => p.address || '—' },
    { key: 'status', header: 'Estado', render: (p) => (
      p.isCurrentAccountant ? (
        <span className="badge badge-success">mi gestoría</span>
      ) : p.isAccountant ? (
        <span className="badge badge-info">gestoría</span>
      ) : (
        <span className={`badge ${p.isActive !== false ? 'badge-success' : 'badge-gray'}`}>
          {p.isActive !== false ? 'activo' : 'inactivo'}
        </span>
      )
    ) },
    { key: 'actions', header: '', className: 'col-actions', render: (p) => (
      <div className="dt-actions">
        <button className="btn-icon" onClick={() => openEdit(p)} title="Editar">
          <Edit2 size={16} />
        </button>
        <button className="btn-icon" onClick={() => setProviderToDelete(p.id)} title="Borrar" style={{ color: '#ef4444' }}>
          <Trash2 size={16} />
        </button>
      </div>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Proveedores</h1>
          <p className="page-subtitle">{providers.length} proveedores registrados</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={18} /> Nuevo proveedor
        </button>
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <Search />
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre, NIF, email o teléfono..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Truck size={64} />
            <h3>No hay proveedores</h3>
            <p>Añade tu primer proveedor para empezar</p>
          </div>
        ) : viewMode === 'table' ? (
          <DataTable columns={fl.orderColumns(columns)} rows={filtered} hiddenKeys={fl.hidden} />
        ) : (
          <div className="items-grid">
            {filtered.map(provider => (
              <div className="item-card" key={provider.id}>
                <div className="item-card-header">
                  <div className="item-card-avatar">{(provider.name || '?').charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="item-card-title"><span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.8em' }}>#{provider.id}</span> {provider.name}</div>
                    {!fl.isHidden('city') && provider.city && <div className="item-card-subtitle">{provider.city}</div>}
                  </div>
                  {!fl.isHidden('status') && (
                    provider.isCurrentAccountant ? (
                      <span className="badge badge-success">mi gestoría</span>
                    ) : provider.isAccountant ? (
                      <span className="badge badge-info">gestoría</span>
                    ) : (
                      <span className={`badge ${provider.isActive !== false ? 'badge-success' : 'badge-gray'}`}>
                        {provider.isActive !== false ? 'activo' : 'inactivo'}
                      </span>
                    )
                  )}
                </div>
                <div className="item-card-body">
                  {!fl.isHidden('nif') && provider.nif && (
                    <div className="item-card-row" style={fl.cardStyle('nif')}>
                      <span className="item-label">NIF</span>
                      <span>{provider.nif}</span>
                    </div>
                  )}
                  {!fl.isHidden('email') && provider.email && (
                    <div className="item-card-row" style={fl.cardStyle('email')}>
                      <span className="item-label">Email</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{provider.email}</span>
                    </div>
                  )}
                  {!fl.isHidden('phone') && provider.phone && (
                    <div className="item-card-row" style={fl.cardStyle('phone')}>
                      <span className="item-label">Tel</span>
                      <span>{provider.phone}</span>
                    </div>
                  )}
                  {!fl.isHidden('address') && provider.address && (
                    <div className="item-card-row" style={fl.cardStyle('address')}>
                      <span className="item-label">Dirección</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{provider.address}</span>
                    </div>
                  )}
                </div>
                <div className="item-card-footer">
                  <div className="item-card-actions">
                    <button className="btn-icon" onClick={() => openEdit(provider)} title="Editar">
                      <Edit2 size={16} />
                    </button>
                    <button className="btn-icon" onClick={() => setProviderToDelete(provider.id)} title="Borrar" style={{ color: '#ef4444' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Editar proveedor' : 'Nuevo proveedor'}</h2>
              <button className="btn-icon" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nombre *</label>
                    <input className="form-input" value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">NIF/CIF</label>
                    <input className="form-input" value={form.nif}
                      onChange={e => setForm({ ...form, nif: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-input" value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Teléfono</label>
                    <input className="form-input" value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Dirección</label>
                  <input className="form-input" placeholder="Calle y número" value={form.address}
                    onChange={e => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Ciudad</label>
                    <input className="form-input" value={form.city}
                      onChange={e => setForm({ ...form, city: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Código postal</label>
                    <input className="form-input" value={form.postalCode}
                      onChange={e => setForm({ ...form, postalCode: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Provincia</label>
                    <input className="form-input" value={form.province}
                      onChange={e => setForm({ ...form, province: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <select className="form-input" value={form.isActive ? 'activo' : 'inactivo'}
                      onChange={e => setForm({ ...form, isActive: e.target.value === 'activo' })}>
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox"
                      checked={form.isAccountant || form.isCurrentAccountant}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm({ ...form, isAccountant: checked, isCurrentAccountant: checked ? form.isCurrentAccountant : false });
                      }} />
                    Es una gestoría / asesoría
                  </label>
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: (form.isAccountant || form.isCurrentAccountant) ? 1 : 0.5 }}>
                    <input type="checkbox"
                      checked={form.isCurrentAccountant}
                      disabled={!(form.isAccountant || form.isCurrentAccountant)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm({ ...form, isCurrentAccountant: checked, isAccountant: checked ? true : form.isAccountant });
                      }} />
                    Es mi gestoría actual (al activar, se desmarca cualquier otra que lo estuviera)
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label">Notas</label>
                  <textarea className="form-input" value={form.notes}
                    onChange={e => setForm({ ...form, notes: e.target.value })} />
                </div>
                {editing && (
                  <OwnerSelect
                    value={form.ownerId}
                    onChange={(id) => setForm({ ...form, ownerId: id })}
                  />
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar cambios' : 'Crear proveedor'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {providerToDelete !== null && (
        <div className="modal-overlay" onClick={() => setProviderToDelete(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar borrado</h2>
              <button className="btn-icon" onClick={() => setProviderToDelete(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>¿Borrar este proveedor? Esta acción no se puede deshacer.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => setProviderToDelete(null)}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={() => handleDelete(providerToDelete)}>Borrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Providers;
